// src/components/dashboard/candidate/ProfileJourneyModal.tsx
// Two-phase signup modal: Phase 1 uses shared ProfileStepsForm (steps 1-6),
// Phase 2 handles account creation (email/password or Google OAuth).
import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, Sparkles, UserPlus } from 'lucide-react';
import type { CandidateProfileCreatePayload } from '@/lib/models/candidateProfile';
import { useAuth } from '@/contexts/AuthContext';
import { useCandidateProfile } from '@/contexts/CandidateProfileContext';
import { useNavigate } from 'react-router-dom';
import CONFIG from '@/lib/config';
import { CODE_USER_ALREADY_EXISTS } from '@/lib/api/authErrors';

import { ProfileStepsForm } from './ProfileStepsForm';
import { UserInfoStep } from './journey-steps/7UserInfoStep';
import { CelebrationModal } from './journey-steps/CelebrationModal';
import { IslandSlideOver } from '@/components/custom-ui/IslandSlideOver';

// Must match the keys in ProfileStepsForm so we can restore progress on "Back"
const PROFILE_FORM_STORAGE_KEY = 'nexilink_profile_steps_progress';
const PROFILE_FORM_STEP_KEY = 'nexilink_profile_steps_step';
const OAUTH_PROFILE_KEY = 'oauth_profile_data';

interface ProfileJourneyModalProps {
    isOpen: boolean;
    onClose: () => void;
    /** #320 — CandidateDashboard passed this (to close the modal and refresh the
     *  user's profile) but the component never declared or called it, so finishing
     *  the journey left the dashboard showing the OLD profile until a manual
     *  reload. Optional because the embedded/signup mode has no parent to notify. */
    onComplete?: () => void | Promise<void>;
    /**
     * Take the user to the login form. Signing up with an address you already
     * registered is a normal thing to do by accident, and "det finns redan ett
     * konto" is only useful if there is a door next to it.
     *
     * The email is passed so that form can be seeded with it — retyping an
     * address typed seconds ago is pure friction. Email only, never the
     * password.
     */
    onSwitchToLogin?: (prefillEmail?: string) => void;
    mode?: 'modal' | 'embedded';
}

interface AccountFormData {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    /** #324 — user ticked the terms/privacy consent. Never defaulted to true. */
    acceptedTerms: boolean;
}

export const ProfileJourneyModal: React.FC<ProfileJourneyModalProps> = ({
    isOpen,
    onClose,
    onComplete,
    onSwitchToLogin,
    mode = 'modal',
}) => {
    const [phase, setPhase] = useState<'profile' | 'account'>('profile');
    const [profilePayload, setProfilePayload] = useState<CandidateProfileCreatePayload | null>(null);
    const [accountData, setAccountData] = useState<AccountFormData>({
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        acceptedTerms: false,
    });
    /** #324 — only after a blocked submit, so the box is not red before they
        have had a chance to tick it. */
    const [showConsentError, setShowConsentError] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showCelebration, setShowCelebration] = useState(false);
    const [error, setError] = useState<string | null>(null);
    /** Set when the failure was "that address already has an account". */
    const [existingAccount, setExistingAccount] = useState(false);
    /** From the signup response — drives what the celebration promises. */
    const [createdVerified, setCreatedVerified] = useState(false);

    const { register, initiateGoogleLogin, isAuthenticated } = useAuth();
    const { createUserProfile } = useCandidateProfile();
    const navigate = useNavigate();

    // Restore to account phase if profile payload was stored (e.g. page refresh during step 7)
    useEffect(() => {
        if (isOpen) {
            const stored = localStorage.getItem(OAUTH_PROFILE_KEY);
            if (stored) {
                try {
                    setProfilePayload(JSON.parse(stored));
                    setPhase('account');
                } catch {
                    localStorage.removeItem(OAUTH_PROFILE_KEY);
                }
            }
        }
    }, [isOpen]);

    const clearAllProgress = () => {
        localStorage.removeItem(PROFILE_FORM_STORAGE_KEY);
        localStorage.removeItem(PROFILE_FORM_STEP_KEY);
        localStorage.removeItem(OAUTH_PROFILE_KEY);
    };

    // Called when ProfileStepsForm completes steps 1-6
    const handleProfileComplete = async (payload: CandidateProfileCreatePayload) => {
        setProfilePayload(payload);

        // An already signed-in candidate (CandidateDashboard's "komplettera din
        // profil") has an account — sending them to the account phase asked them
        // to sign up a second time with the address they are logged in with, so
        // the journey could only end in "det finns redan ett konto". They get the
        // profile saved against the account they already have instead.
        if (isAuthenticated) {
            setIsSubmitting(true);
            setError(null);
            try {
                const created = await createUserProfile(payload);
                if (!created) throw new Error('Profilen kunde inte sparas. Försök igen.');
                clearAllProgress();
                setShowCelebration(true);
            } catch (err: any) {
                setError(err?.message || 'Profilen kunde inte sparas. Försök igen.');
            } finally {
                setIsSubmitting(false);
            }
            return;
        }

        localStorage.setItem(OAUTH_PROFILE_KEY, JSON.stringify(payload));
        setPhase('account');
    };

    // Go back from account step to the profile form
    const handleBackToProfile = () => {
        setError(null);
        // Restore profile data into ProfileStepsForm's localStorage so it resumes at the last step
        if (profilePayload) {
            localStorage.setItem(PROFILE_FORM_STORAGE_KEY, JSON.stringify(profilePayload));
            localStorage.setItem(PROFILE_FORM_STEP_KEY, '5');
        }
        localStorage.removeItem(OAUTH_PROFILE_KEY);
        setPhase('profile');
    };

    const updateAccountData = (updates: Partial<AccountFormData>) => {
        setAccountData(prev => ({ ...prev, ...updates }));
        setError(null);
        setExistingAccount(false);
    };

    // Google OAuth signup
    const handleGoogleSignup = async () => {
        if (!profilePayload) return;
        // #324 — same gate as the email path. Google signup creates the same
        // account under the same terms, so leaving it ungated would make the
        // consent checkbox opt-out in practice: click Google, skip the box.
        if (!accountData.acceptedTerms) {
            setShowConsentError(true);
            setError('Du behöver godkänna villkoren för att skapa ett konto.');
            return;
        }
        try {
            const result = await initiateGoogleLogin(CONFIG.GOOGLE_OAUTH_CALLBACK_PATH);
            if (result.success && result.googleUrl) {
                // Profile payload is already stored under OAUTH_PROFILE_KEY
                window.location.href = result.googleUrl;
            } else {
                throw new Error('Kunde inte initiera Google-inloggning');
            }
        } catch (err: any) {
            setError(err?.message || 'Ett fel uppstod vid Google-inloggning. Försök igen.');
        }
    };

    // Email/password signup
    const handleSubmit = async () => {
        if (!profilePayload) return;
        // #324 — checked before isSubmitting is set, so a blocked attempt does
        // not leave the button spinning.
        if (!accountData.acceptedTerms) {
            setShowConsentError(true);
            setError('Du behöver godkänna villkoren för att skapa ett konto.');
            return;
        }
        setIsSubmitting(true);
        setError(null);
        setExistingAccount(false);

        try {
            const { email, password, firstName, lastName } = accountData;

            const missingFields: string[] = [];
            if (!firstName) missingFields.push('förnamn');
            if (!lastName) missingFields.push('efternamn');
            if (!email) missingFields.push('e-post');
            if (!password) missingFields.push('lösenord');
            if (missingFields.length > 0) {
                throw new Error(`Vänligen fyll i: ${missingFields.join(', ')}`);
            }

            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(email)) {
                throw new Error('Ogiltig e-postadress');
            }
            if (password.length < 8) {
                throw new Error('Lösenordet måste vara minst 8 tecken');
            }

            const result = await register(
                email,
                password,
                'candidate',
                firstName,
                lastName,
                profilePayload,
            );

            if (result.success) {
                clearAllProgress();
                setCreatedVerified(result.isVerified ?? false);
                // No timer, and no navigate(): the celebration used to promise
                // "Omdirigerar till din dashboard..." and then send the user to
                // the landing page — while not signed in. It now waits for the
                // user to press Logga in, and dismissing is what moves them on.
                setShowCelebration(true);
            } else {
                if (result.error?.code === CODE_USER_ALREADY_EXISTS) setExistingAccount(true);
                throw new Error(result.error?.message || 'Registreringen misslyckades. Försök igen.');
            }
        } catch (err: any) {
            setError(err?.message || 'Ett fel uppstod när kontot skulle skapas. Försök igen.');
        } finally {
            setIsSubmitting(false);
        }
    };

    /** Celebration dismissed — hand back to the parent, then offer login. */
    const handleCelebrationContinue = () => {
        setShowCelebration(false);
        // Before onClose: the parent's handler closes the modal AND refreshes the
        // profile, so calling it first means the dashboard re-renders with the
        // new data rather than the stale copy.
        void onComplete?.();
        // Signed-in users are already where they need to be; only a brand-new
        // account gets sent to the login form.
        if (!isAuthenticated && onSwitchToLogin) {
            onSwitchToLogin(accountData.email);
            return;
        }
        if (mode === 'modal') onClose();
        if (!isAuthenticated) navigate('/');
    };

    const celebration = (
        <CelebrationModal
            isOpen={showCelebration}
            variant={isAuthenticated ? 'profile' : 'signup'}
            isVerified={createdVerified}
            email={accountData.email}
            onContinue={handleCelebrationContinue}
        />
    );

    if (!isOpen && mode === 'modal') return null;

    // ── Account creation step (phase 2) UI pieces ──

    const accountContent = (
        <>
            {error && (
                <div className="mb-6 bg-red-50 border-2 border-red-200 rounded-xl p-3">
                    <p className="text-red-700 text-sm">{error}</p>
                    {existingAccount && onSwitchToLogin && (
                        <button
                            type="button"
                            /* Wrapped, not passed directly: onClick hands the
                               handler a MouseEvent, which would arrive as the
                               prefill address. The address is what we want —
                               this is the "you already have an account" path,
                               so the one thing we know is which email it is. */
                            onClick={() => onSwitchToLogin(accountData.email)}
                            className="mt-2 text-sm font-semibold text-[#1A4D4F] hover:underline"
                        >
                            Logga in i stället →
                        </button>
                    )}
                </div>
            )}
            <UserInfoStep
                formData={accountData}
                updateFormData={updateAccountData}
                onGoogleSignup={handleGoogleSignup}
                showConsentError={showConsentError}
            />
        </>
    );

    const accountNavigation = (
        <div className="border-t-2 border-gray-100 bg-gray-50 px-6 sm:px-8 py-4">
            <div className="flex items-center justify-between">
                <button
                    onClick={handleBackToProfile}
                    className="flex items-center gap-1.5 px-4 py-2.5 text-gray-700 hover:bg-gray-200 rounded-xl transition-all font-medium text-sm"
                >
                    <ChevronLeft className="w-4 h-4" />
                    Tillbaka
                </button>

                <p className="hidden sm:block text-xs font-medium text-[#1A4D4F]">
                    Sista steget!
                </p>

                <button
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="flex items-center gap-2 px-5 py-2.5 bg-[#1A4D4F] text-[#f5f5f5] font-semibold rounded-xl hover:bg-[#1A4D4F]/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                    {isSubmitting ? (
                        <>
                            <div className="w-4 h-4 border-2 border-[#f5f5f5] border-t-transparent rounded-full animate-spin" />
                            Skapar konto...
                        </>
                    ) : (
                        <>
                            Skapa konto & profil
                            <Sparkles className="w-4 h-4" />
                        </>
                    )}
                </button>
            </div>
        </div>
    );

    // ── Embedded mode ──

    if (mode === 'embedded') {
        if (phase === 'profile') {
            return (
                <div className="flex flex-col h-full">
                    <div className="flex-1 overflow-y-auto p-6">
                        <ProfileStepsForm
                            onComplete={handleProfileComplete}
                            submitLabel="Fortsätt"
                            submittingLabel="Laddar..."
                        />
                    </div>
                    {celebration}
                </div>
            );
        }

        return (
            <div className="flex flex-col h-full">
                <div className="flex-1 overflow-y-auto px-6 py-5">
                    {accountContent}
                </div>
                {accountNavigation}
                {celebration}
            </div>
        );
    }

    // ── Modal mode ──

    return (
        <IslandSlideOver onClose={onClose} widthClass="max-w-2xl">
            {phase === 'profile' ? (
                <>
                    <div className="flex justify-end p-4 flex-shrink-0">
                        <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
                            <X className="w-5 h-5 text-gray-500" />
                        </button>
                    </div>
                    <div className="flex-1 overflow-y-auto px-6 pb-6">
                        <ProfileStepsForm
                            onComplete={handleProfileComplete}
                            submitLabel="Fortsätt"
                            submittingLabel="Laddar..."
                        />
                    </div>
                </>
            ) : (
                <>
                    {/* Header */}
                    <div className="flex-shrink-0 p-5 text-center relative">
                        <button onClick={onClose} className="absolute top-3 right-3 p-2 hover:bg-gray-100 rounded-lg">
                            <X className="w-5 h-5 text-gray-500" />
                        </button>
                        <div className="w-12 h-12 mx-auto mb-2 bg-[#A4C3B2] rounded-2xl flex items-center justify-center">
                            <UserPlus className="w-6 h-6 text-[#1A4D4F]" />
                        </div>
                        <h2 className="text-lg font-bold">Slutför registrering</h2>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto px-5 pb-4">
                        {accountContent}
                    </div>

                    {/* Footer */}
                    {accountNavigation}
                </>
            )}

            {celebration}
        </IslandSlideOver>
    );
};
